import { useEffect, useRef } from 'react';

import { FcmNotificationType } from '@/modules/notifications';
import { NotificationPayload } from '@/modules/notifications/types/notification-payload';

import notificationService from '../firebase-notification.service';

/**
 * React hook for subscribing to Firebase Cloud Messaging notifications
 *
 * Provides a declarative way to listen for specific notification types within
 * React components. Automatically handles subscription setup and cleanup on
 * component mount/unmount.
 *
 * @template TData - Type of custom data payload in notification
 * @param notificationType - The notification type to subscribe to
 * @param handler - Callback function invoked when matching notifications arrive
 *
 * @example Subscribe with typed data
 * ```typescript
 * interface InvoiceData {
 *   invoiceId: number;
 *   amount: number;
 * }
 *
 * function InvoicesPage() {
 *   useNotifications<InvoiceData>(FCMNotificationType.INVOICE_READY, (notification) => {
 *     console.log(notification.data.invoiceId); // ✅ Type-safe
 *     console.log(notification.data.amount);    // ✅ Type-safe
 *   });
 * }
 * ```
 *
 * @example Subscribe without typed data
 * ```typescript
 * function NotificationHandler() {
 *   useNotifications(FCMNotificationType.ALL, (notification) => {
 *     console.log('Data:', notification.data); // Record<string, unknown>
 *   });
 * }
 * ```
 */
export const useNotifications = <TData extends Record<string, unknown>>(
  notificationType: FcmNotificationType,
  handler: (notification: NotificationPayload<TData>) => void
): void => {
  // Store handler in ref to avoid re-subscribing when handler changes
  const handlerRef = useRef(handler);

  // Update ref when handler changes (allows handler to use latest component state)
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Subscribe to notifications and cleanup on unmount or type change
  useEffect(() => {
    const unsubscribe = notificationService.subscribe<TData>(notificationType, notification =>
      handlerRef.current(notification)
    );

    return () => unsubscribe();
  }, [notificationType]);
};
